import { useTheme } from '@mui/material';
import clsx from 'clsx';
import React, { createRef, MouseEvent, RefObject, useEffect, useState } from 'react';

import type { Theme } from '../../../../themes/themes';
import { Button } from '../../../Button/Button';
import { ScrollBar } from '../ScrollBar';
import { getStyles } from './Navigation.styles';

interface Link {
  text: string;
  anchor: string;
  ref?: RefObject<HTMLButtonElement>;
}

export interface NavigationProps {
  links: Link[];
  active?: string;
  onLinkClicked?(event: MouseEvent<HTMLButtonElement>, anchor: string): void;
  variant: 'white' | 'transparent';
}

export const Navigation: React.FC<NavigationProps> = ({ links, onLinkClicked, active, variant }) => {
  const theme = useTheme<Theme>();
  const styles = getStyles({ theme });
  const [selectedElement, setSelectedElement] = useState<HTMLElement>();
  const linksWithRef: Link[] = links.map((link) => ({
    ...link,
    ref: createRef(),
  }));

  const findButtonElement = (anchor: string) => linksWithRef.find((link) => link.anchor === anchor);

  const handleLinkClicked = (anchor: string) => (event: MouseEvent<HTMLButtonElement>) => onLinkClicked(event, anchor);

  useEffect(() => {
    const button = findButtonElement(active);
    if (button) {
      setSelectedElement(button.ref.current);
    }
  }, [active, linksWithRef]);

  return (
    <ScrollBar scrollIntoView={selectedElement}>
      {linksWithRef.map((link: Link) => {
        const buttonStyles = Object.assign(
          {},
          styles.button[variant],
          link.anchor === active && link.anchor !== null && styles.buttonActive[variant]
        );

        return (
          <Button
            component="a"
            key={link.anchor}
            href={`#${link.anchor}`}
            onClick={handleLinkClicked(link.anchor)}
            sx={buttonStyles}
            ref={link.ref}
            className={clsx('InPageNaviButton-Opener')}
            color="inherit"
          >
            {link.text}
          </Button>
        );
      })}
    </ScrollBar>
  );
};
